import { FacilityCompletedLevels } from '@/store/types/facility'
import { IFacility, IUser, IPayerType, EOrigin } from '@zunta/configurations'

type FacilityParams = {
  facility: IFacility;
  users: IUser[];
  facilitiesActivePayerTypesMap: { [k: string]: IPayerType[] } | null;
  contactListType: { [k: string]: string[] }
};

function getFacilityBatchLevels ({
  facility,
  contactListType
}: Pick<FacilityParams, 'facility' | 'contactListType'>) {
  return (facility.residentMailContacts || []).filter(contact =>
    (contactListType[facility.id as number] || []).includes(contact)
  ).length
    ? 100
    : 0
}

function residentBillingContactCheck (
  billingContact: number | IUser | undefined,
  compare: number[]
) {
  return billingContact
    ? compare.includes(billingContact as number) ||
        compare.includes((billingContact as IUser).id)
    : false
}

function getActivePayersForFacilityLevel ({
  facility,
  facilitiesActivePayerTypesMap
}: Pick<FacilityParams, 'facility' | 'facilitiesActivePayerTypesMap'>) {
  const hasActivePayers = !!(
    facilitiesActivePayerTypesMap &&
    (facilitiesActivePayerTypesMap[facility.id as number] || []).length
  )
  return hasActivePayers ? 100 : 0
}

function getFacilityCompleteLevel ({
  facility,
  users,
  facilitiesActivePayerTypesMap,
  contactListType
}: FacilityParams) {
  if (!facility) return 0
  const completedLevels = { generalInfo: 0, batches: 0, payers: 0 }
  const userIds = users
    .filter(user => {
      return (
        user.isAllFacilities ||
        (user.facilities &&
          user.facilities
            .map(facilityId => Number(facilityId))
            .includes(facility.id as number)) ||
        residentBillingContactCheck(facility.residentBillingContact, [user.id])
      )
    })
    .map(user => user.id)
  completedLevels.generalInfo = getFacilityGeneralInfoCompleteLevel(
    facility,
    userIds
  )
  completedLevels.batches = getFacilityBatchLevels({
    facility,
    contactListType
  })
  completedLevels.payers = getActivePayersForFacilityLevel({
    facility,
    facilitiesActivePayerTypesMap
  })
  const sum =
    completedLevels.batches +
    completedLevels.generalInfo +
    completedLevels.payers
  return Math.floor(sum / 3)
}

function getFacilityGeneralInfoCompleteLevel (
  facility: IFacility,
  availableUsersIds: number[]
) {
  if (!facility) return 0
  let completed = 0
  const hasDirectory = facility.directory
  const hasOriginId = facility.originid
  const isPccOrigin = facility.origin === EOrigin.PCC
  const nonPccOriginFields: string[] =
    !hasDirectory && !hasOriginId
      ? ['directory', 'originid']
      : ([hasDirectory && 'directory', hasOriginId && 'originid'].filter(
          Boolean
        ) as string[])
  const requiredFields = [
    'name',
    'state',
    'city',
    'street',
    'zip',
    'logo',
    'phone',
    'residentBillingContact',
    ...(isPccOrigin ? ['username'] : nonPccOriginFields)
  ]
  requiredFields.forEach((key: string) => {
    const increment =
      key === 'residentBillingContact'
        ? residentBillingContactCheck(
            facility.residentBillingContact,
            availableUsersIds
          )
        : !!facility[key as keyof IFacility]
    increment && completed++
  })
  return Math.floor((completed / requiredFields.length) * 100)
}

function getFacilityCompleteLevelObject ({
  facility,
  users,
  facilitiesActivePayerTypesMap,
  contactListType
}: FacilityParams): {
  type: keyof FacilityCompletedLevels;
  value: number;
}[] {
  return [
    {
      type: 'batch',
      value: getFacilityBatchLevels({ facility, contactListType })
    },
    {
      type: 'generalInfo',
      value: getFacilityGeneralInfoCompleteLevel(
        facility,
        users.map(user => user.id)
      )
    },
    {
      type: 'payers',
      value: getActivePayersForFacilityLevel({
        facility,
        facilitiesActivePayerTypesMap
      })
    }
  ]
}
export {
  getFacilityCompleteLevel,
  getFacilityGeneralInfoCompleteLevel,
  getFacilityCompleteLevelObject
}
